/**
 * @module agent/adapters/services/SearchHandler
 *
 * Local search functionality for agent sessions.
 * Searches through session titles and message content.
 *
 * Extracted from AgentCoreAdapter to reduce complexity.
 *
 * @example
 * import SearchHandler from './services/SearchHandler';
 *
 * const handler = new SearchHandler({
 *   sessionManager,
 *   messageManager
 * });
 *
 * const results = await handler.search('database');
 */

/**
 * Handles local search operations across sessions and messages.
 */
class SearchHandler {
  /**
   * @param {Object} deps - Dependencies
   * @param {Object} deps.sessionManager - SessionManager instance
   * @param {Object} deps.messageManager - MessageManager instance
   */
  constructor({ sessionManager, messageManager }) {
    if (!sessionManager) {
      throw new Error('sessionManager is required');
    }
    if (!messageManager) {
      throw new Error('messageManager is required');
    }

    this._sessionManager = sessionManager;
    this._messageManager = messageManager;
  }

  /**
   * Search sessions and messages for matching content.
   * Main entry point for search functionality.
   *
   * Fix #4: Removed async - no async operations in implementation.
   *
   * @param {string} query - Search query
   * @param {Object} [options] - Search options
   * @param {number} [options.limit=50] - Maximum results to return (Fix #7)
   * @returns {Array} Array of search results sorted by similarity score
   */
  search(query, options = {}) {
    if (!query?.trim()) {
      return [];
    }

    const { limit = 50 } = options;
    return this._localSearch(query.trim(), limit);
  }

  /**
   * Perform local search across sessions and messages.
   * Searches session titles and message content (case-insensitive).
   *
   * @param {string} query - Search query (already trimmed)
   * @param {number} limit - Maximum results to return (Fix #7)
   * @returns {Array} Array of search results
   * @security Fix #12: Result text contains raw message content - consumers MUST
   *           sanitize before rendering to prevent XSS. Consider using DOMPurify
   *           or React's built-in escaping when displaying search results.
   * @private
   */
  _localSearch(query, limit) {
    const results = [];
    const term = query.toLowerCase();

    for (const [sessionId, session] of this._sessionManager.entries()) {
      // Search session title
      if (session.title?.toLowerCase().includes(term)) {
        results.push({
          sessionId,
          text: session.title,
          source: 'session_title',
          similarityScore: 1
        });
      }

      // Search messages in this session
      const messages = this._messageManager.getForSession(sessionId);
      for (const msg of messages) {
        // Skip hidden messages
        if (msg.hidden) {
          continue;
        }

        // Skip messages without content
        if (!msg.content) {
          continue;
        }

        if (msg.content.toLowerCase().includes(term)) {
          results.push({
            sessionId,
            text: msg.content.substring(0, 200),
            source: msg.type === 'user' ? 'user_message' : 'ai_response',
            similarityScore: 0.8
          });
        }
      }
    }

    // Sort by similarity score (highest first) and apply limit (Fix #7)
    const sorted = [...results].sort((a, b) => b.similarityScore - a.similarityScore);
    return sorted.slice(0, limit);
  }
}

export default SearchHandler;
